// Pure Week view model (architecture sprint-04 §6.2). Always 7 rows Mon..Sun.
import { addDays, type LocalDate } from '@shared/calendar';
import { NO_PROJECT_LABEL } from '@shared/palette';
import type { EntryListItem } from '@/types/entries';

const DAYS_PER_WEEK = 7;
/** The bar scale never goes below 8h, so a light week does not look full. */
export const MIN_WEEK_SCALE_SECONDS = 8 * 3600;

export interface WeekSegment {
  key: string;
  label: string;
  color: string | null;
  archived: boolean;
  seconds: number;
}

export interface WeekRow {
  date: LocalDate;
  totalSeconds: number;
  /** Seconds desc. */
  segments: WeekSegment[];
  entries: EntryListItem[];
}

export interface WeekModel {
  rows: WeekRow[];
  totalSeconds: number;
  scaleSeconds: number;
  /** Projects present this week (legend), by total desc. */
  legend: WeekSegment[];
}

function addToSegments(map: Map<string, WeekSegment>, e: EntryListItem): void {
  const key = e.projectId ?? 'none';
  const seg = map.get(key) ?? {
    key,
    label: e.project?.name ?? NO_PROJECT_LABEL,
    color: e.project?.color ?? null,
    archived: e.project?.status === 'archived',
    seconds: 0,
  };
  seg.seconds += e.durationSeconds;
  map.set(key, seg);
}

function bySecondsDesc(a: WeekSegment, b: WeekSegment): number {
  return b.seconds - a.seconds || a.label.localeCompare(b.label, 'en', { sensitivity: 'base' });
}

export function buildWeek(items: EntryListItem[], start: LocalDate): WeekModel {
  const legendMap = new Map<string, WeekSegment>();
  const rows: WeekRow[] = [];
  for (let i = 0; i < DAYS_PER_WEEK; i += 1) {
    const date = addDays(start, i);
    const entries = items.filter((e) => e.date === date);
    const segMap = new Map<string, WeekSegment>();
    for (const e of entries) {
      addToSegments(segMap, e);
      addToSegments(legendMap, e);
    }
    const segments = [...segMap.values()].sort(bySecondsDesc);
    rows.push({ date, totalSeconds: segments.reduce((acc, s) => acc + s.seconds, 0), segments, entries });
  }
  const maxDay = rows.reduce((acc, r) => Math.max(acc, r.totalSeconds), 0);
  return {
    rows,
    totalSeconds: rows.reduce((acc, r) => acc + r.totalSeconds, 0),
    scaleSeconds: Math.max(MIN_WEEK_SCALE_SECONDS, maxDay),
    legend: [...legendMap.values()].sort(bySecondsDesc),
  };
}
